import { Upload as UploadAntd } from 'antd'
import { clsx } from 'clsx'
import { useLatestCallback } from 'hook/useLatestCallback'
import { DragEvent, ReactNode, useRef, useState } from 'react'
import { IMAGE_TYPES } from 'util/image'
import css from './Upload.module.scss'

type Accept = 'image'

type Props = {
  accept: Accept
  children?: ReactNode
  multiple?: boolean
  onClick?(): void
  onFile?(file: File): void | Promise<void>
  wide?: boolean
}

function getAccept(accept: Accept | undefined): string | undefined {
  switch (accept) {
    case 'image':
      return IMAGE_TYPES.join(',')
    default:
      return undefined
  }
}

export function Upload(props: Props) {
  const { accept, children, multiple, onClick, onFile, wide } = props
  const [active, setActive] = useState(false)
  const className = clsx(css.upload, active && css.active, wide && css.wide)
  const acc = getAccept(accept)
  const ref = useRef<HTMLDivElement>(null)

  const beforeUpload = useLatestCallback(async (file: File): Promise<false> => {
    if (!onFile) return false
    if (file) await onFile(file)
    return false
  })

  const onDragEnter = useLatestCallback((_event: DragEvent) => {
    setActive(true)
  })

  const onDragLeave = useLatestCallback((event: DragEvent<HTMLDivElement>) => {
    const target = ref.current
    if (!target) return
    const out = !target.contains(event.relatedTarget as Node)
    if (out) setActive(false)
  })

  return (
    <div
      className={className}
      ref={ref}
      onClick={onClick}
      onDragEnter={onDragEnter}
      onDragLeave={onDragLeave}
    >
      <UploadAntd
        accept={acc}
        fileList={[]}
        multiple={multiple}
        beforeUpload={beforeUpload}
      >
        {children}
      </UploadAntd>
    </div>
  )
}
